import { useCallback, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi, getStoredToken, storeToken } from "@/api/client";
import {
  queryKeys,
  useCreateSession,
  useLogin,
  useRegister,
} from "@/api/queries";
import type { RegisterRequest, SessionResponse } from "@/api/types";
import { AuthContext, type AuthContextValue } from "./AuthContext";

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const hasToken = getStoredToken() !== null;

  const { data: principal, isLoading } = useQuery({
    queryKey: queryKeys.me,
    queryFn: () => authApi.me(),
    enabled: hasToken,
    retry: false,
    staleTime: 60_000,
  });

  const createSession = useCreateSession();
  const loginMutation = useLogin();
  const registerMutation = useRegister();

  const startSession = useCallback(
    (session: SessionResponse) => {
      storeToken(session.access_token);
      queryClient.setQueryData(queryKeys.me, session.principal);
      void queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
    [queryClient],
  );

  const loginWithPassword = useCallback(
    async (email: string, password: string) => {
      startSession(await loginMutation.mutateAsync({ email, password }));
    },
    [loginMutation, startSession],
  );

  const register = useCallback(
    async (payload: RegisterRequest) => {
      startSession(await registerMutation.mutateAsync(payload));
    },
    [registerMutation, startSession],
  );

  const login = useCallback(
    async (userId: string, roles?: string[]) => {
      const session = await createSession.mutateAsync({
        user_id: userId,
        roles,
      });
      storeToken(session.access_token);
      void queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
    [createSession, queryClient],
  );

  const logout = useCallback(() => {
    void authApi.logout().catch(() => undefined);
    storeToken(null);
    void queryClient.setQueryData(queryKeys.me, null);
    void queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      principal: principal ?? null,
      isLoading: hasToken && isLoading,
      isAuthenticated: !!principal,
      login,
      loginWithPassword,
      register,
      logout,
    }),
    [
      principal,
      hasToken,
      isLoading,
      login,
      loginWithPassword,
      register,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
